import React from 'react';
import FormInput from '../../FormInput';

const ProcesVerbalForm = ({
  formData,
  formErrors,
  selectedPV,
  onInputChange,
  onSubmit,
  submitting,
  soutenances,
  procesVerbaux = []
}) => {
  const mentions = [
    { value: 'PASSABLE', label: 'Passable' },
    { value: 'ASSEZ_BIEN', label: 'Assez bien' },
    { value: 'BIEN', label: 'Bien' },
    { value: 'TRES_BIEN', label: 'Très bien' },
    { value: 'EXCELLENT', label: 'Excellent' }
  ];

  const decisions = [
    { value: 'ADMIS', label: 'Admis' },
    { value: 'ADMIS_RESERVE', label: 'Admis avec réserve' },
    { value: 'AJOURNE', label: 'Ajourné' },
    { value: 'REPORTE', label: 'Reporté' }
  ];

  const getSoutenanceLabel = (soutenance) => {
    if (!soutenance) return 'Non définie';
    
    let etudiantsNoms = '';
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      const etudiant = soutenance.etudiants_soutenance[0].etudiant;
      etudiantsNoms = `${etudiant.nom} ${etudiant.prenom}`;
    } else if (soutenance.etudiant_principal_info) {
      etudiantsNoms = `${soutenance.etudiant_principal_info.nom} ${soutenance.etudiant_principal_info.prenom}`;
    }
    
    return etudiantsNoms || soutenance.titre_travail?.substring(0, 40) || 'Soutenance';
  };

  // Filtrer les soutenances éligibles (date passée et sans PV existant)
  const getSoutenancesEligibles = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return soutenances.filter(soutenance => {
      // Vérifier si la date est passée
      const soutenanceDate = new Date(soutenance.date_soutenance);
      const datePassee = soutenanceDate <= today;
      
      // Vérifier si elle n'a pas déjà un PV
      const dejaUnPV = procesVerbaux.some(pv => pv.soutenance?.id === soutenance.id);
      
      return datePassee && !dejaUnPV;
    });
  };

  const soutenancesEligibles = getSoutenancesEligibles();

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Informations générales */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Informations générales</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <FormInput
              label="Soutenance"
              type="select"
              name="soutenance"
              value={formData.soutenance}
              onChange={onInputChange}
              options={[
                { value: '', label: soutenancesEligibles.length > 0 ? 'Sélectionner une soutenance' : 'Aucune soutenance éligible' },
                ...soutenancesEligibles.map(s => ({ value: s.id, label: getSoutenanceLabel(s) }))
              ]}
              required
              error={formErrors.soutenance}
              disabled={soutenancesEligibles.length === 0}
            />
            {soutenancesEligibles.length === 0 && (
              <p className="mt-1 text-sm text-gray-500">
                Aucune soutenance éligible. Les soutenances doivent avoir eu lieu et ne pas avoir de procès-verbal existant.
              </p>
            )}
          </div>

          <FormInput
            label="Note finale (/20)"
            type="number"
            name="note_finale"
            value={formData.note_finale}
            onChange={onInputChange}
            placeholder="15.5"
            min="0"
            max="20"
            step="0.1"
            required
            error={formErrors.note_finale}
          />

          <FormInput
            label="Mention"
            type="select"
            name="mention"
            value={formData.mention}
            onChange={onInputChange}
            options={mentions.map(m => ({ value: m.value, label: m.label }))}
            required
            error={formErrors.mention}
          />

          <FormInput
            label="Décision"
            type="select"
            name="decision"
            value={formData.decision}
            onChange={onInputChange}
            options={decisions.map(d => ({ value: d.value, label: d.label }))}
            required
            error={formErrors.decision}
          />

          <div className="md:col-span-2">
            <FormInput
              label="Observations"
              type="textarea"
              name="observations"
              value={formData.observations}
              onChange={onInputChange}
              placeholder="Observations du jury..."
              rows={3}
              error={formErrors.observations}
            />
          </div>

          <div className="md:col-span-2">
            <FormInput
              label="Recommandations"
              type="textarea"
              name="recommandations"
              value={formData.recommandations}
              onChange={onInputChange}
              placeholder="Recommandations pour l'étudiant..."
              rows={3}
              error={formErrors.recommandations}
            />
          </div>
        </div>
      </div>

      {/* Signatures du jury */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Signatures du jury</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormInput
            label="Date signature président"
            type="date"
            name="date_signature_president"
            value={formData.date_signature_president}
            onChange={onInputChange}
            error={formErrors.date_signature_president}
          />

          <FormInput
            label="Date signature rapporteur"
            type="date"
            name="date_signature_rapporteur"
            value={formData.date_signature_rapporteur}
            onChange={onInputChange}
            error={formErrors.date_signature_rapporteur}
          />

          <FormInput
            label="Date signature examinateur"
            type="date"
            name="date_signature_examinateur"
            value={formData.date_signature_examinateur}
            onChange={onInputChange}
            error={formErrors.date_signature_examinateur}
          />
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="valide"
              checked={formData.valide}
              onChange={onInputChange}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700">
              Marquer comme validé (définitif)
            </span>
          </label>
        </div>
      </div>

    </form>
  );
};

export default ProcesVerbalForm;